/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
import React, { useMemo, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { select } from '@panel/commons/core/themev2';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Radio from '@panel/commons/lib/Input/InputRadio';
import Alert from '@panel/commons/lib/Alert/Alert';
import { sendTrackingInfo } from '@panel/commons';
import { ecommerceSites, addUrlParam } from '@panel/commons/helpers';
import Cookies from 'js-cookie';
import { getColor } from '@panel/commons/core/themev3';
import { creditsSelectors, creditsOperations } from '../../../ducks/credits';
import { PublishOperations } from '../../../ducks/publish';
import Atencion from '../../../assets/img/atencion.svg';
import {
  getCredits,
  getPublisherByPosting,
  getInsufficientCreditsByBranch,
  filterCredits,
  getSameActivePlanPostings,
  getSameBranchId
} from '../../../helpers/utils';

const PublishModal = ({
  onClose,
  publicationType,
  operationType,
  postings, // Array de los postingId's seleccionados
  device,
  isDraft,
  redirectToEdit,
  postingList, // En bulk trae un array de objetos con todos los postings del paginado. Desde la card trae un array con el objeto del posting en cuestión.
  isIntegrator,
  publisherTypeId,
  postingsInfoSelected
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ecommercePath = ecommerceSites(publisherTypeId, postingList[0] ? postingList[0].postingId : null);
  const { credits, publicationPlans } = useSelector(creditsSelectors.getCreditsState);
  const { publishPosting } = PublishOperations;
  const { selectCredit } = creditsOperations;
  const isDevelopment = publicationType !== 'Clasificado';
  // La constante publisherId se usa sólo cuando tenemos un sólo aviso seleccionado en el bulk o cuando se republica desde la card:
  const publisherId = postingList.reduce((pId, currentPosting) => {
    if (postings[0] === currentPosting.postingId) {
      return currentPosting.publisherId;
    }
    return pId;
  }, '');
  const usingGlobalCredits = credits?.branchCredits?.length > 1 && postings.length > 1;
  const hasDisabledBranch = useMemo(() => {
    if (!publisherId || !credits?.branchCredits?.length) return false;
    return !credits.branchCredits.some(b => parseInt(b.branchId, 10) === parseInt(publisherId, 10));
  }, [publisherId, credits]);
  const branchCredits = useMemo(() => getCredits(postings, publisherId, credits), [postings, publisherId, credits]);
  const postingAndPublisherId = useMemo(() => getPublisherByPosting(postingList, postings), [postingList, postings]);
  const sameActivePlanPostings = useMemo(() => getSameActivePlanPostings(postingAndPublisherId, postingsInfoSelected), [
    postingAndPublisherId,
    postingsInfoSelected
  ]);
  const sameBranchIdPostings = useMemo(() => getSameBranchId(postingAndPublisherId), [postingAndPublisherId]);
  const insufficientCreditsByBranch = useMemo(
    () =>
      usingGlobalCredits ? getInsufficientCreditsByBranch(credits, postingAndPublisherId, postingsInfoSelected) : {},
    [credits, postingAndPublisherId, usingGlobalCredits, postingsInfoSelected]
  );
  const { insufficientCredits, postingsAvailable } = insufficientCreditsByBranch;

  // Se evalúa que alguno de los postings sea isExclusiveZone = true
  const hasExclusiveZone = postingList.filter(e => postings.includes(e.postingId)).some(s => s.isExclusiveZone);
  // Se evalúa que alguno de los postings sea isExclusiveZone = false
  const hasNotExclusiveZone = postingList.filter(e => postings.includes(e.postingId)).some(s => !s.isExclusiveZone);
  // mixExclusivePostings nos indica que en la selección del bulk tenemos avisos mixtos (exclusivos y no exclusivos)
  const mixExclusivePostings = hasExclusiveZone && hasNotExclusiveZone;
  // Se evalúa que todos los isExclusiveZone sean true.
  const everyAreExclusiveZone = postingList.filter(e => postings.includes(e.postingId)).every(s => s.isExclusiveZone);
  // Se toman los ids de la exclusiveZoneId de cada posting seleccionado y suman a un array con los Ids de zonas exclusivas:
  const arrayWithExclusiveZoneId = postingList.filter(p => postings.includes(p.postingId)).map(e => e.exclusiveZoneId);

  let AvailableCredits = filterCredits(
    branchCredits?.creditItems,
    isDevelopment,
    operationType,
    true,
    hasExclusiveZone,
    arrayWithExclusiveZoneId,
    credits?.exclusivePlans,
    mixExclusivePostings
  );

  const forbidenPlansForSaleFilter = () => {
    // se filtran los postings de la lista con la lista de posting ids
    const currentPostingsList = postingList.filter(posting => postings.includes(posting.postingId));
    // se toman los planes de alquiler que no se pueden usar en venta
    const forbidenPlans = publicationPlans && publicationPlans['2'];
    // reviso si tiene operacion de venta en algun posting
    const hasSaleOperation =
      currentPostingsList.length > 0 &&
      currentPostingsList.some(
        posting => posting.pricesOperationTypes && posting.pricesOperationTypes.some(op => op.operationType === 1)
      );
    // filtro los planes prohibidos de los creditos disponibles
    if (hasSaleOperation && forbidenPlans) {
      forbidenPlans.map(
        // eslint-disable-next-line array-callback-return
        plan => {
          if (plan) {
            AvailableCredits = AvailableCredits.filter(
              credit => plan.publication_plan_id !== credit.publicationPlanId.toString()
            );
          }
        }
      );
    }
  };

  forbidenPlansForSaleFilter();

  const publish = (postingsArray, userCredits) => {
    onClose();
    const publishData = {
      postingIds: postingsArray,
      postingAndPublisherId,
      publicationPlan: userCredits.reduce((selectedPlan, currentPlan) => {
        if (currentPlan.checked) selectedPlan = currentPlan.publicationPlanId;
        return selectedPlan;
      }, '')
    };
    dispatch(publishPosting(publishData));
  };

  const redirectToEcommerce = () => {
    window.location.href = `${window.location.origin}${addUrlParam(
      ecommercePath,
      everyAreExclusiveZone ? 'ref=zonademand' : null
    )}`;
  };
  const noCredits = AvailableCredits.length === 0;

  const getNoCreditsExclusiveZoneText = () => {
    // Caso bulk:
    if (postings.length > 1) {
      // Todos los postings son de Zona Demand:
      if (everyAreExclusiveZone) return '.bulk.everyExclusiveZone';
      // Algunos postings son de Zona Demand:
      if (hasExclusiveZone) return '.bulk.someExclusiveZone';
      // No hay postings de Zona Demand:
      return '.bulk';
    }
    // Caso bulk con un único aviso seleccionado o desde la Card:
    return hasExclusiveZone ? '.exclusiveZone' : '';
  };

  const getTextInfo = () => {
    // Con planes disponibles:
    if (!noCredits) {
      // Algunos postings o todos son de Zona Demand:
      if (hasExclusiveZone) {
        return (
          t(`panel.avisos.publish.modal.text.isExclusiveZone${postings.length > 1 ? '.bulk' : ''}`) +
          t(`panel.avisos.publish.modal.text${postings.length > 1 ? '.bulk' : ''}`)
        );
      }
      // No hay postings de Zona Demand:
      return t(`panel.avisos.publish.modal.text${postings.length > 1 ? '.bulk' : ''}`);
    }
    // Sin planes disponibles:
    return t(`panel.avisos.publish.modal.text.no.credits${getNoCreditsExclusiveZoneText()}`);
  };

  useEffect(() => {
    const statusPanel = Cookies.get('statusPanel');
    if (!statusPanel && isIntegrator) {
      Cookies.set('statusPanel', true, {
        expires: 1
      });
    }
  }, [isIntegrator]);

  // Si todos los avisos seleccionados pertenecen a una única sucursal (branchId), se valida que el plan seleccionado tenga menos créditos disponibles que la cantidad total de avisos en el bulk, excluyendo los que están ONLINE con el mismo tipo de plan:
  const hasDisabledItems = c =>
    sameBranchIdPostings &&
    c.availableItems < postings.length - (sameActivePlanPostings?.[sameBranchIdPostings]?.[c.publicationPlanId] || 0);

  // Se calcula el total de avisos posbiles a publicar de todas la sucursales, descontando los ONLINE del mismo tipo de plan:
  const getNumberOfPostingsAvailable = c =>
    postingsAvailable
      ? Object.keys(postingsAvailable).reduce((resultTotal, branchId) => {
          const { necessaryCredits, totalSameActivePlan } = postingsAvailable[branchId];
          // Se calcula el total de avisos a publicar (necessaryCredits-totalSameActivePlan) por sucursal:
          const totalPostingsToPublish = necessaryCredits - (totalSameActivePlan?.[c.publicationPlanId] || 0);
          // Se realiza la sumatoria de la cantidad de los avisos a publicar de todas la sucursales:
          resultTotal += totalPostingsToPublish;
          return resultTotal;
        }, 0)
      : 0;

  // Se valida que no se tengan créditos suficientes por Sucursal (branch), exceptuando los ONLINE del mismo tipo de plan:
  const hasInsufficientCreditsByBranch = c => {
    return (
      // Si hay avisos seleccionados de distintas sucursales y alguna de las sucursales no tiene créditos suficientes o tiene un límite de avisos activos, no se permitirá la publicación:
      insufficientCredits?.some(element => element.planId === c.publicationPlanId) ||
      // Si la sumatoria del total de avisos a publicar de todas las sucursales supera la cantidad de créditos disponibles globales no se permitirá la publicación:
      c.availableItems < getNumberOfPostingsAvailable(c)
    );
  };

  const publishButtonDisabled =
    !AvailableCredits.some(c => c.checked) ||
    AvailableCredits.some(c => c.checked && (hasDisabledItems(c) || hasInsufficientCreditsByBranch(c)));

  const handleChangeBranch = () => {
    const postingId = postings[0];
    if (!postingId) return;
    const publisherIdOrigen = publisherId ? `&publisherIdOrigen=${publisherId}` : '';
    window.location.href = `${window.location.origin}/panel/publicador-profesionales/publish?postingId=${postingId}&isEdition=true&republish=true${publisherIdOrigen}`;
  };

  return (
    <Container device={device}>
      {(AvailableCredits.length === 0 || isDraft) && !hasDisabledBranch && (
        <Img data-panel='panel-modal-publish-img' src={Atencion} />
      )}
      {!isDraft ? (
        <Title data-panel='panel-modal-publish-title'>
          {!noCredits || hasDisabledBranch
            ? t(`panel.avisos.publish.modal.title${postings.length > 1 ? '.bulk' : ''}`, { value: postings.length })
            : t('panel.avisos.publish.modal.title.no.credits')}
        </Title>
      ) : (
        <Title data-panel='panel-modal-publish-title'>{t('panel.avisos.publish.modal.draft.title')}</Title>
      )}
      {!isDraft && hasDisabledBranch && (
        <DisabledBranchWarning data-panel='panel-modal-publish-disabledBranch-warning'>
          {t('panel.avisos.publish.modal.disabledBranch.warning')}
        </DisabledBranchWarning>
      )}
      {!isDraft && !hasDisabledBranch && <Text data-panel='panel-modal-publish-text'>{getTextInfo()}</Text>}
      {isDraft && (
        <DraftMessage data-panel='panel-modal-publish-text'>{t('panel.avisos.publish.modal.draft.text')}</DraftMessage>
      )}
      {!noCredits && !isDraft && !hasDisabledBranch && (
        <SubTitle data-panel='panel-modal-publish-subtitle'>{t('panel.avisos.publish.modal.subtitle')}</SubTitle>
      )}
      {!isDraft &&
        !hasDisabledBranch &&
        AvailableCredits.map(c => (
          <PlanLayout key={`key-radio-planId-${c.publicationPlanId}`}>
            <PlanContainer
              onClick={() => dispatch(selectCredit(c.publicationPlanId, usingGlobalCredits ? 'global' : publisherId))}
              disabled={c.checked && (hasDisabledItems(c) || hasInsufficientCreditsByBranch(c))}
              device={device}
              data-panel='panel-modal-publish-plan'
            >
              <label htmlFor={`radio-planId-${c.publicationPlanId}`}>
                <Radio
                  name={`radio-planId-${c.publicationPlanId}`}
                  checked={c.checked || false}
                  onChange={e => {
                    e.stopPropagation();
                    dispatch(selectCredit(c.publicationPlanId, usingGlobalCredits ? 'global' : publisherId));
                  }}
                  marginRight='8'
                />
                <span className={`publishModal-planName-${device}`}>{c.name}</span>
              </label>
              <AvailableCreditsText>
                {c.availableItems === 999999
                  ? 'Ilimitado'
                  : `${c.availableItems} ${t('panel.avisos.publish.modal.available')}`}
              </AvailableCreditsText>
              {c.checked && hasDisabledItems(c) && !hasInsufficientCreditsByBranch(c) && (
                <NotEnoughCredits>{t('panel.avisos.publish.modal.not.enough.plans')}</NotEnoughCredits>
              )}
            </PlanContainer>
            {c.checked && hasInsufficientCreditsByBranch(c) && (
              <NotEnoughCredits isBulk>{t('panel.avisos.publish.modal.not.enough.plans.bulk')}</NotEnoughCredits>
            )}
          </PlanLayout>
        ))}
      {isIntegrator && !hasDisabledBranch && <Alert message={t('panel.avisos.publish.modal.alert.integrador')} />}
      <ButtonContainer>
        {hasDisabledBranch && !isDraft ? (
          <Button
            data-panel='panel-modal-publish-cta-changeBranch'
            className='PUBLISH-MODAL-BUTTON-CTA'
            noEcommerce
            onClick={handleChangeBranch}
          >
            {t('panel.avisos.publish.modal.disabledBranch.cta')}
          </Button>
        ) : (
          <>
            {ecommercePath && !isDraft && (
              <Button
                data-panel='panel-modal-publish-cta-buyPlan'
                onClick={() => {
                  sendTrackingInfo('Panel', 'Mis avisos', 'Republicar Aviso-Comprar plan');
                  redirectToEcommerce();
                }}
                noPlans={AvailableCredits.length === 0}
              >
                {t('panel.avisos.publish.modal.CTA1')}
              </Button>
            )}
            {!isDraft ? (
              <Button
                data-panel='panel-modal-publish-cta-publish'
                className='PUBLISH-MODAL-BUTTON-CTA'
                disabled={publishButtonDisabled}
                onClick={() => {
                  sendTrackingInfo('Panel', 'Mis avisos', 'Republicar Aviso-Republicar');
                  publish(postings, AvailableCredits);
                }}
                noPlans={AvailableCredits.length === 0}
                noEcommerce={!ecommercePath}
              >
                {t('panel.avisos.publish.modal.CTA2')}
              </Button>
            ) : (
              <Button
                data-panel='panel-modal-publish-cta-edit'
                className='PUBLISH-MODAL-BUTTON-CTA'
                onClick={() => {
                  redirectToEdit();
                }}
                noEcommerce
              >
                {t('panel.avisos.publish.modal.draft.cta')}
              </Button>
            )}
          </>
        )}
      </ButtonContainer>
    </Container>
  );
};

export default PublishModal;

const Container = styled.div`
  padding: 48px 32px 32px 32px;
  width: ${({ device }) => device !== 'mobile' && '400px'};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h1`
  font-family: Hind;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px;
  letter-spacing: 0em;
  text-align: center;
  margin-bottom: 8px;
  white-space: pre-line;
`;

const Text = styled.div`
  font-family: Hind;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0em;
  text-align: center;
  margin-bottom: 24px;
  white-space: pre-wrap;
`;

const DisabledBranchWarning = styled.div`
  align-self: stretch;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 12px 16px;
  margin-top: 10px;
  border-radius: 8px;
  border: 1px solid ${select('yellow')};
  background-color: #fffae6;
  font-family: Hind;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  text-align: left;
  color: ${select('darkGray')};
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: normal;
`;

const SubTitle = styled.div`
  font-family: Hind;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0em;
  text-align: left;
  color: ${select('darkGray')};
  margin-bottom: 16px;
  align-self: flex-start;
`;

const DraftMessage = styled.div`
  font-family: Hind;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0em;
  text-align: center;
  color: ${select('darkGray')};
  margin-bottom: 16px;
  align-self: center;
  white-space: pre-wrap;
`;

const PlanLayout = styled.div`
  width: 100%;
  margin-bottom: 16px;
  :last-of-type {
    margin-bottom: 32px;
  }
`;

const PlanContainer = styled.span`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  > label {
    font-family: Hind;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 26px;
    letter-spacing: 0em;
    display: flex;
    align-items: center;
    color: ${({ disabled }) => disabled && select('darkGray')};
    > div {
      position: relative;
      top: -1px;
    }
    > span {
      &.publishModal-planName-mobile {
        max-width: 190px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      &.publishModal-planName-desktop {
        max-width: 240px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      &.publishModal-planName-tablet {
        max-width: 240px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
`;

const NotEnoughCredits = styled.div`
  font-family: Hind;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0em;
  color: ${select('negative')};
  position: absolute;
  top: 20px;
  right: 0px;
  white-space: break
    ${({ isBulk }) =>
      isBulk &&
      css`
        white-space: break-spaces;
        position: relative;
        top: 4px;
        left: 25px;
        margin-right: 22px;
        text-align: justify;
      `};
`;

const AvailableCreditsText = styled.div`
  font-family: Hind;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0em;
  text-align: right;
  color: ${select('midDarkGray')};
`;

const ButtonContainer = styled.div`
  width: 100%;
  min-width: 0;
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  justify-self: flex-end;
  margin-top: 25px;
`;

const Button = styled.button`
  width: ${({ noPlans }) => (!noPlans ? '140px' : '100%')};
  height: 40px;
  border-radius: 8px;
  border: 1px solid ${getColor('primary')};
  padding: 0px 12px;
  font-family: Hind;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 21px;
  letter-spacing: 0em;
  cursor: ${({ disabled }) => !disabled && 'pointer'};
  :hover {
    background-color: ${getColor('tertiary')};
  }
  &.PUBLISH-MODAL-BUTTON-CTA {
    background-color: ${({ disabled }) => (!disabled ? getColor('primary') : select('lightGray'))};
    border-color: ${({ disabled }) => disabled && select('lightGray')};
    color: ${({ disabled }) => (!disabled ? 'white' : select('midLightGray'))};
    display: ${({ noPlans }) => noPlans && 'none'};
    width: ${({ noEcommerce }) => noEcommerce && '100%'};
    :hover {
      background-color: ${({ disabled }) => !disabled && getColor('secondary')};
    }
  }
`;

const Img = styled.div`
  background-image: url(${({ src }) => src});
  width: 145px;
  height: 96px;
  margin-bottom: 32px;
`;
